import { Link } from "react-router";
import { ContactLink, LegalLayout } from "./PrivacyPage";

const UPDATED = "18 October 2026";

/**
 * The acceptable-use terms everyone agrees to before their first chat message
 * (version 1: `TERMS_VERSION` in server/src/chat/safety.ts; raising it makes
 * everyone agree again). Keep it in step with what blocking, reporting and
 * the operator's tools actually do (goals/chat-event-circles/DESIGN.md,
 * "Blocks, reports and terms").
 */
export function TermsPage() {
  return (
    <LegalLayout title="Chat terms">
      <p className="muted">Last updated {UPDATED}</p>

      <p>
        ten20&apos;s chats are for the people in your circles: a chat for each circle and direct messages between people
        who share one. Before you send your first message you agree to these terms. They apply to everything you post:
        messages, replies and reactions.
      </p>

      <h2>Keep it respectful</h2>
      <p>Don&apos;t use ten20&apos;s chats to post or send:</p>
      <ul>
        <li>
          harassment, bullying, threats or intimidation, including unwanted messages after someone asks you to stop;
        </li>
        <li>
          hate speech: attacks on people for their race, ethnicity, religion, disability, sex, gender, sexual
          orientation or similar;
        </li>
        <li>violent threats, or content that celebrates or encourages violence;</li>
        <li>sexual content involving minors, or sexual content sent to anyone who hasn&apos;t asked for it;</li>
        <li>content that encourages suicide or self-harm;</li>
        <li>spam, scams, phishing or malware;</li>
        <li>anything illegal, or other people&apos;s private information without their permission;</li>
        <li>impersonation of someone else.</li>
      </ul>
      <p>Don&apos;t try to get around a block, a removed message or a suspension, for example with another account.</p>

      <h2>Reporting and blocking</h2>
      <ul>
        <li>
          <strong>Report</strong> a message from its menu (&quot;⋯&quot;), or a person from the menu of your direct chat
          with them, choosing a reason and adding a note if you like. The report goes to us; a message report includes a
          copy of the message, so it stays as evidence even if the message is later edited or unsent, and a report about
          a person keeps the profile photo they had then until we close it. The person you report is not told.
        </li>
        <li>
          <strong>Block</strong> someone from a message&apos;s menu or your direct chat. Neither of you can then send
          the other direct messages, you get no notifications from them, and their messages in shared circle chats are
          collapsed for you. They are not told, but they can see that the direct chat no longer takes messages. You can
          unblock them on your account page.
        </li>
        <li>
          Blocking only affects chat. To stop sharing your location with someone, leave the circle you share or pause
          sharing with it.
        </li>
        <li>
          If you think someone is in danger, contact your local emergency services first: we cannot respond in real
          time.
        </li>
      </ul>

      <h2>What circle admins can do</h2>
      <p>Admins of a circle can remove any message from that circle&apos;s chat and remove members from the circle.</p>
      <p>
        In a family circle, admins can also offer to supervise a member who isn&apos;t an admin. Supervision starts only
        when that member accepts it, and gives the guardians it names no access to chats: they see no more of a chat
        than any other member, and nothing of other chats or circles. Guardians can pause the member&apos;s sharing with
        their circle, share an approximate area for them there and approve or decline the member&apos;s requests; the
        member is told every time and can stop supervision on their own after a notice period. How supervision affects
        location sharing, and what guardians see, is in the <Link to="/privacy">privacy policy</Link>.
      </p>

      <h2>What we can do</h2>
      <ul>
        <li>
          Messages are not end-to-end encrypted; they are stored on our server so they reach every device you sign in
          on. We read a message only when it has been reported to us or the law requires it.
        </li>
        <li>
          When we act on a report we may remove the message or the profile photo, for everyone, or suspend the account
          that broke these terms: a suspended account is signed out on every device, its integration tokens stop working
          and it cannot sign in again unless we lift the suspension. We may also dismiss a report and take no action.
        </li>
        <li>We may report illegal content to the authorities and respond to lawful requests for information.</li>
      </ul>

      <h2>Your messages</h2>
      <p>
        You are responsible for what you post, and you keep any rights you have in it. You let us store your messages
        and deliver them, and their notifications, to the people in the chat. How long messages are kept and who can see
        them is in the <Link to="/privacy">privacy policy</Link>.
      </p>

      <h2>Changes</h2>
      <p>
        If we change these terms we will update the date above, and you will be asked to agree again before your next
        message.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about these terms or a report: <ContactLink />.
      </p>
    </LegalLayout>
  );
}
